import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Papaparse from 'papaparse';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import MonitorRoundedIcon from '@mui/icons-material/MonitorRounded';
import Typography from '@mui/material/Typography';
import { API_STATUS, KpiStatus, REPORT_KEYS } from '../../constants';
import { fadeInDuration } from '../../theme';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  clearReports,
  fetchReports,
  reportsSelectors,
} from '../../redux/reports';
import {
  closeCurrentPeriod,
  fetchCheckApproved,
  fetchPeriods,
  periodsSelectors,
} from '../../redux/periods';
import { DepartmentBaseType, PeriodType } from '../../types';
import {
  formatYYYYMMDateToMMMMYString,
  numberFormat,
  is,
  formatYYYYMMDate,
} from '../../utils';
import { BottomPanel, Loader, PageWrapper } from '../../components';
import Filters from './Filters';
import PreviewModal from './PreviewModal';
import { downloadCsv, getFinalBonus } from './Reports.utils';
import { Badge, Box, useTheme } from '@mui/material';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { userDataSelectors } from '../../redux/userData';
import { PageTitle } from '../../utils/pageTitles';
import { useTitle } from '../../hooks/useTitle';
import { capitalizeFirstLetter } from '../globalTracker/utils';
import {
  CompensationType,
  GroupedData,
  ReportType,
  TypeOfReport,
} from '../../types/reports';
import { DateTime } from 'luxon';

const Reports = (): JSX.Element => {
  const setPageTitle = useTitle();
  useEffect(() => {
    setPageTitle(PageTitle.reports);
  }, [setPageTitle]);
  const dispatch = useAppDispatch();

  const { spacing } = useTheme();

  const reportsData = useAppSelector(reportsSelectors.getReports);
  const currentPeriod = useAppSelector(periodsSelectors.getCurrentPeriod);
  const reportsApiStatus = useAppSelector(reportsSelectors.getReportsApiStatus);
  const periodsApiStatus = useAppSelector(periodsSelectors.getPeriodsApiStatus);
  const isPeriodsLoading = periodsApiStatus === API_STATUS.LOADING;
  const isLoading = reportsApiStatus === API_STATUS.LOADING;
  const isFailed = reportsApiStatus === API_STATUS.FAILED;

  const [period, setPeriod] = useState<PeriodType | null>(null);

  const user = useAppSelector(userDataSelectors.getUserData);
  const [typeOfReport, setTypeOfReport] = useState<TypeOfReport>(
    user?.permissions.canGenerateReport
      ? TypeOfReport.METRICS
      : TypeOfReport.TEAM_COMPENSATION_PLAN,
  );

  const [locations, setLocations] = useState<string[]>([]);
  const [rootDepartments, setRootDepartments] = useState<DepartmentBaseType[]>(
    [],
  );
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [totalUnapprovedReports, setTotalUnapprovedReports] =
    useState<number>(0);

  const handleTogglePreview = () => setShowPreview((prevState) => !prevState);

  const transformData = useCallback(
    (data: ReportType[]) => {
      if (typeOfReport === TypeOfReport.METRICS) {
        return data;
      }
      const groupedData = data.reduce((acc: GroupedData, item: ReportType) => {
        if (!acc[item.fullName]) {
          acc[item.fullName] = {
            summary: {
              ...item,
              metricName: '',
              frequency: '',
              bestCase: null,
              worstCase: null,
              bonusShare: 0,
              isGroupedDataHeading: true,
              kpiOwner: '',
            },
            details: [],
          };
        }

        acc[item.fullName].summary.bonusShare += item.bonusShare || 0;
        acc[item.fullName].details.push({
          ...item,
          isGroupedDataDetail: true,
        });

        return acc;
      }, {} as GroupedData);

      return Object.values(groupedData).flatMap(({ summary, details }) => {
        if (details.length > 1) {
          return [summary, ...details];
        }
        details.forEach((detail) => {
          detail.isGroupedDataDetail = false;
        });
        return details; // Skip the summary row if there’s only one record
      });
    },
    [typeOfReport],
  );
  const reports: ReportType[] = useMemo(
    () => transformData(reportsData),
    [reportsData, transformData],
  );

  const hasTrueUp = useMemo(() => {
    return !!(reports || []).find(({ trueup }) => !is.nullOrUndefined(trueup));
  }, [reports]);

  const handleDownloadCsv = () => {
    const trueUpColumns = hasTrueUp
      ? [REPORT_KEYS.trueUp, REPORT_KEYS.finalBonus]
      : [];

    const reportData = reports.map((report) => {
      const finalBonus = getFinalBonus(report);

      return {
        [REPORT_KEYS.bonus]: `$${numberFormat(report.payout)}`,
        [REPORT_KEYS.trueUp]: is.nullOrUndefined(report.trueup)
          ? numberFormat(report.trueup)
          : `$${numberFormat(report.trueup)}`,
        [REPORT_KEYS.finalBonus]: is.nullOrUndefined(finalBonus)
          ? numberFormat(finalBonus)
          : `$${numberFormat(finalBonus)}`,
        [REPORT_KEYS.bonusType]: report.bonusType,
        [REPORT_KEYS.department]: report.department.replaceAll('&amp;', '&'),
        [REPORT_KEYS.fullName]: report.fullName,
        [REPORT_KEYS.metricName]: report.metricName,
        [REPORT_KEYS.location]: report.location,
        [REPORT_KEYS.period]: formatYYYYMMDateToMMMMYString(report.period),
        [REPORT_KEYS.frequency]: capitalizeFirstLetter(report.frequency),
        [REPORT_KEYS.value]: report.value,
      };
    });
    const reportFieldsOrder = [
      REPORT_KEYS.department,
      REPORT_KEYS.bonusType,
      REPORT_KEYS.fullName,
      REPORT_KEYS.metricName,
      REPORT_KEYS.period,
      REPORT_KEYS.bonus,
      ...trueUpColumns,
      REPORT_KEYS.location,
      REPORT_KEYS.frequency,
      REPORT_KEYS.value,
    ];
    const csvData = Papaparse.unparse(reportData, {
      quotes: true,
      columns: reportFieldsOrder,
    });
    const csvFilename = `KPI report for ${formatYYYYMMDateToMMMMYString(
      period?.period,
    )}.csv`;
    downloadCsv(csvData, csvFilename);
  };

  const handleDownloadCompensationCsv = () => {
    let departmentName = '';

    const parsedReportData: any[] = [
      { [REPORT_KEYS.fullName]: reportsData[0]?.department },
    ];
    for (const report of reportsData) {
      if (departmentName !== report.subDepartment) {
        departmentName = report.subDepartment;
        parsedReportData.push({
          [REPORT_KEYS.fullName]: `> ${report.subDepartment}`,
        });
      }

      // prettier-ignore
      parsedReportData.push({
        [REPORT_KEYS.fullName]: report.fullName,
        [REPORT_KEYS.role]: report.role,
        [REPORT_KEYS.Seniority]: report.seniority,
        [REPORT_KEYS.kpiMetricName]: report.metricName,
        [REPORT_KEYS.frequency]: report.frequency ? capitalizeFirstLetter(report.frequency) : '',
        [REPORT_KEYS.createdAt]: report.createdAt ? DateTime.fromISO(report.createdAt).toFormat('yyyy/MM') : '',
        [REPORT_KEYS.department]: report.department,
        [REPORT_KEYS.reportingTo]: report.reportingToUser,
        [REPORT_KEYS.kpiOwner]: report.kpiOwner,
        [REPORT_KEYS.worstCase]: report.worstCase,
        [REPORT_KEYS.bestCase]: report.bestCase,
        [REPORT_KEYS.targetBonus]: is.nullOrUndefined(report.bonusShare) ? '' : `$${numberFormat(report.bonusShare, ',', 0)}`,
        [REPORT_KEYS.baseCompensation]: is.nullOrUndefined(
          report.baseCompensation,
        ) ? '' : `$${numberFormat(report.baseCompensation, ',', report.compensationType === CompensationType.HOURLY ? 2 : 0, )}`,
        [REPORT_KEYS.compensationType]: report.compensationType ? capitalizeFirstLetter(report.compensationType) : '',
        [REPORT_KEYS.month]: formatYYYYMMDate(report.period, 'MMMM'),
        [REPORT_KEYS.year]: formatYYYYMMDate(report.period, 'yyyy'),
        [REPORT_KEYS.location]: report.location,
        [REPORT_KEYS.averageMonthlyHours]: report.averageMonthlyHours,
      })
    }

    const csvData = Papaparse.unparse(parsedReportData, {
      quotes: true,
      columns: [
        REPORT_KEYS.fullName,
        REPORT_KEYS.role,
        REPORT_KEYS.Seniority,
        REPORT_KEYS.kpiMetricName,
        REPORT_KEYS.frequency,
        REPORT_KEYS.createdAt,
        REPORT_KEYS.worstCase,
        REPORT_KEYS.bestCase,
        REPORT_KEYS.targetBonus,
        REPORT_KEYS.baseCompensation,
        REPORT_KEYS.compensationType,
        REPORT_KEYS.month,
        REPORT_KEYS.year,
        REPORT_KEYS.location,
        REPORT_KEYS.averageMonthlyHours,
        REPORT_KEYS.department,
        REPORT_KEYS.reportingTo,
        REPORT_KEYS.kpiOwner,
      ],
    });
    const csvFilename = `Compensation report for ${formatYYYYMMDateToMMMMYString(
      period?.period,
    )}.csv`;
    downloadCsv(csvData, csvFilename);
  };

  const isClosePeriodAllowed = useAppSelector(
    periodsSelectors.getIsClosePeriodAvailable,
  );

  const handleCloseCurrentPeriod = async () => {
    await dispatch(closeCurrentPeriod());
  };

  useEffect(() => {
    setPeriod(currentPeriod);
  }, [currentPeriod, isPeriodsLoading]);

  useEffect(() => {
    dispatch(fetchPeriods());
    dispatch(fetchCheckApproved());
  }, [dispatch]);

  useEffect(() => {
    if (period && typeOfReport) {
      dispatch(
        fetchReports({
          period: period.period,
          locations: !locations.length ? undefined : locations, // backend will not filter by locations if we send an empty one
          departmentsIds: rootDepartments.length
            ? rootDepartments.map((x) => x.id)
            : undefined,
          typeOfReport,
        }),
      );
    }
    return () => {
      dispatch(clearReports());
    };
  }, [dispatch, locations, period, rootDepartments, typeOfReport]);

  useEffect(() => {
    const countedRows = new Set();
    setTotalUnapprovedReports(
      reportsData.filter((x) => {
        if (typeOfReport === TypeOfReport.METRICS) {
          return x.status !== KpiStatus.Approved;
        }
        if (countedRows.has(x.fullName)) return false;
        countedRows.add(x.fullName);
        return true;
      }).length,
    );
  }, [reportsData, typeOfReport]);

  return (
    <PageWrapper>
      <Card
        variant="outlined"
        sx={{
          maxWidth: 400,
          marginX: 'auto',
          padding: 3,
          borderRadius: `${spacing(1)} ${spacing(1)} 0 0`,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant="h2" mx={2} mt={2}>
            Reports
          </Typography>
          <Filters
            period={period}
            locations={locations}
            rootDepartments={rootDepartments}
            typeOfReport={typeOfReport}
            setPeriod={setPeriod}
            setLocations={setLocations}
            setRootDepartments={setRootDepartments}
            setTypeOfReport={setTypeOfReport}
          />
          {isLoading && (
            <Grid
              item
              xs={12}
              sx={{
                position: 'relative',
                minHeight: (theme) => theme.spacing(6),
              }}
            >
              <Loader />
            </Grid>
          )}
          <Fade
            in={!isLoading}
            timeout={{
              enter: fadeInDuration,
              exit: 0,
            }}
            mountOnEnter
            unmountOnExit
          >
            {isFailed ? (
              <Grid item xs={12}>
                <Alert severity="error">
                  Reports can not be generated
                  <br /> until all submissions are approved
                </Alert>
              </Grid>
            ) : (
              <Grid item xs={12} display="flex">
                <Button
                  fullWidth
                  onClick={
                    typeOfReport === TypeOfReport.METRICS
                      ? handleDownloadCsv
                      : handleDownloadCompensationCsv
                  }
                  startIcon={<DownloadRoundedIcon />}
                  sx={{ minWidth: (theme) => theme.spacing(24), mr: 2 }}
                  variant="contained"
                  disabled={
                    (!!totalUnapprovedReports &&
                      typeOfReport === TypeOfReport.METRICS) ||
                    reports.length === 0
                  }
                >
                  Download .CSV
                </Button>
                <Badge badgeContent={totalUnapprovedReports} color="error">
                  <Button
                    fullWidth
                    onClick={handleTogglePreview}
                    startIcon={<MonitorRoundedIcon />}
                  >
                    Preview
                  </Button>
                </Badge>
              </Grid>
            )}
          </Fade>
          {!!totalUnapprovedReports &&
            typeOfReport === TypeOfReport.METRICS && (
              <Fade
                in={!isLoading}
                timeout={{
                  enter: fadeInDuration,
                  exit: 0,
                }}
                mountOnEnter
                unmountOnExit
              >
                <Grid item xs={12}>
                  <Alert severity="error">
                    Reports can not be generated
                    <br /> until all submissions are approved
                  </Alert>
                </Grid>
              </Fade>
            )}
        </Grid>
      </Card>
      {isClosePeriodAllowed && user?.permissions?.canClosePeriod && (
        <Card
          variant="outlined"
          sx={{
            maxWidth: 400,
            marginX: 'auto',
            borderRadius: `0 0 ${spacing(1)} ${spacing(1)}`,
          }}
        >
          <BottomPanel color={BottomPanel.color.Success} isFixed={false}>
            <Box sx={{ display: 'flex' }}>
              <DoneAllRoundedIcon fontSize="small" />
              <Typography pl={1} variant="body1">
                All results approved
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                onClick={handleCloseCurrentPeriod}
                size="small"
                variant="text"
                sx={{ ml: 1 }}
              >
                Close period
              </Button>
            </Box>
          </BottomPanel>
        </Card>
      )}

      {showPreview && (
        <PreviewModal
          handleDownloadCsv={handleDownloadCsv}
          isOpen={showPreview}
          onClose={handleTogglePreview}
          reports={reports}
          typeOfReport={typeOfReport}
          handleDownloadCompensationCsv={handleDownloadCompensationCsv}
        />
      )}
    </PageWrapper>
  );
};

export default Reports;
